import { addRendition } from '@shared/utils/photoRendition';
import type Player from '@vimeo/player';
import type { StyleSystemProps } from '@withjoy/joykit';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { addError, addSuppressedError } from '@shared/utils/logger';
import { useIsFeatureEnabled } from '@shared/core/featureFlags';
import { useInView } from '@shared/hooks/useInView';
import { useIsUserIdle } from '@shared/hooks/useIsUserIdle';
import { useVimeoPlayerContext } from '../../../../utils/vimeoPlayer';
import { StyledFallbackImage, StyledVideoContainer, styles } from './ProductVideo.styles';

/**
 * Known Vimeo SDK bug: the iframe occasionally sends error postMessage events
 * with missing `name` field, causing `processData` to crash with
 * "undefined is not an object (evaluating 'r.name.includes')".
 * See: https://github.com/vimeo/player.js/issues/1148
 *
 * Known errors are suppressed (no alert). Unknown errors use addError so they
 * surface in New Relic and can trigger alerts.
 */
const KNOWN_VIMEO_SDK_ERROR_PATTERNS = ['r.name.includes', 'name.includes'];

const isKnownVimeoSdkError = (error: unknown): boolean => {
  if (!(error instanceof Error)) return false;
  const message = error.message || '';
  return KNOWN_VIMEO_SDK_ERROR_PATTERNS.some(pattern => message.includes(pattern));
};

const handleVimeoError = (operation: string) => (error: unknown) => {
  if (isKnownVimeoSdkError(error)) {
    addSuppressedError(error, { context: 'ProductVideo', operation });
  } else {
    addError(error, { context: 'ProductVideo', operation });
  }
};

// Matches the shared VimeoVideo: part of the video must be visible, since it is often cropped by its frame.
const PLAY_OBSERVER_OPTIONS: IntersectionObserverInit = { threshold: 0.2 };

/**
 * A product's looping Vimeo video with a fallback image until it is ready. It plays while
 * `shouldPlay` is set (the gallery selects it). With the `vimeoPlaybackOptimizationEnabled` flag on,
 * it also needs to be on screen with an active user, matching the shared `VimeoVideo`; flag off (or
 * loading) keeps today's `shouldPlay`-only playback.
 */
const ProductVideo = ({
  videoUrl,
  shouldPlay,
  style,
  useBackgroundMode,
  fallbackImageUrl,
  forceSquareSize,
}: {
  videoUrl: string;
  shouldPlay?: boolean;
  style?: StyleSystemProps;
  useBackgroundMode?: boolean;
  fallbackImageUrl?: string;
  forceSquareSize?: boolean;
}) => {
  const { VimeoPlayer, prepareForUse } = useVimeoPlayerContext();
  const [player, setPlayer] = useState<Player | null>(null);
  const [isReady, setIsReady] = useState(false);
  const [videoDimensions, setVideoDimensions] = useState({ width: 0, height: 0 });
  const [frameDimensions, setFrameDimensions] = useState({ width: '100%', height: '100%' });
  const iframeRef = useRef<HTMLIFrameElement>(null);
  // The player whose one-time setup (ready, autopause, dimensions) has run; play/pause can toggle often.
  const initializedPlayerRef = useRef<Player | null>(null);

  const [isOptimized, isFlagLoading] = useIsFeatureEnabled('vimeoPlaybackOptimizationEnabled');
  const isUserIdle = useIsUserIdle(isOptimized);
  const { ref: inViewRef, inView } = useInView<HTMLDivElement>(PLAY_OBSERVER_OPTIONS);
  const canPlay = !!shouldPlay && (!isOptimized || (inView && !isUserIdle));

  const embedUrl = useMemo(() => {
    const url = new URL(videoUrl);
    url.searchParams.append('muted', '1');
    url.searchParams.append('loop', '1');
    url.searchParams.append('autoplay', '1');
    if (useBackgroundMode) url.searchParams.append('background', '1');
    return url.toString();
  }, [useBackgroundMode, videoUrl]);

  useEffect(() => {
    prepareForUse();
  }, [prepareForUse]);

  useEffect(() => {
    if (VimeoPlayer && iframeRef?.current) {
      setPlayer(new VimeoPlayer(iframeRef.current));
    }
  }, [VimeoPlayer, iframeRef]);

  // Vimeo Player SDK promise rejections are caught and triaged:
  // - Known SDK bugs (e.g. missing `name` in postMessage data) → suppressed
  // - Unknown errors → reported via addError to trigger NR alerts
  useEffect(() => {
    if (player && canPlay) {
      player.play().catch(handleVimeoError('play'));
      // Setup stays tied to the first play, as before, so an unselected video keeps its fallback image.
      if (initializedPlayerRef.current === player) return;
      initializedPlayerRef.current = player;
      player
        .ready()
        .then(() => {
          setIsReady(true);
          player.setAutopause(false).catch(handleVimeoError('setAutopause'));
          Promise.all([player.getVideoHeight(), player.getVideoWidth()])
            .then(([videoHeight, videoWidth]) => {
              setVideoDimensions({ width: videoWidth, height: videoHeight });
            })
            .catch(handleVimeoError('getVideoDimensions'));
        })
        .catch(handleVimeoError('ready'));
    } else {
      player?.pause().catch(handleVimeoError('pause'));
    }
  }, [player, canPlay]);

  useEffect(() => {
    if (!forceSquareSize || !videoDimensions.height || !videoDimensions.width) return;

    if (videoDimensions.height < videoDimensions.width) {
      const proportion = (videoDimensions.width / videoDimensions.height) * 100;
      setFrameDimensions({ width: proportion + '%', height: '100%' });
      return;
    }

    const proportion = (videoDimensions.height / videoDimensions.width) * 100;
    setFrameDimensions({ width: '100%', height: proportion + '%' });
  }, [forceSquareSize, videoDimensions]);

  return (
    <StyledVideoContainer
      // Also while the flag loads, so `inView` is known before it resolves on.
      ref={isOptimized || isFlagLoading ? inViewRef : undefined}
      __css={style}
      {...styles.videoContainer}
    >
      <iframe
        ref={iframeRef}
        src={embedUrl}
        title={embedUrl}
        frameBorder="0"
        allow="autoplay"
        height={frameDimensions.height}
        width={frameDimensions.width}
        style={{ position: 'relative', left: '50%', transform: 'translateX(-50%)', zIndex: useBackgroundMode ? -1 : 1 }}
      />
      <StyledFallbackImage
        __css={styles.fallbackImage}
        style={{
          backgroundImage: `url(${addRendition({ url: fallbackImageUrl, renditionSize: 'medium' })})`,
          backgroundColor: 'rgba(0,0,0, .03)',
          opacity: `${isReady ? '0' : '1'}`,
        }}
      />
    </StyledVideoContainer>
  );
};

export { ProductVideo };
